import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageLoader from "../components/PageLoader";
import GameweekOpsPage from "../components/admin/competition/GameweekOpsPage";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { fetchMyGameweeks, MyGameweekItem } from "../api/admin";
import { card, widePageContainer, pageTitle, badge, errorStyle } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props {
  user: User;
}

export default function MyGameweeksPage({ user }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [items, setItems] = useState<MyGameweekItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useBreadcrumbs([{ label: t("common.home"), to: "/overview" }, { label: t("myGameweeks.title") }]);

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchMyGameweeks(user)
      .then((data) => setItems(data.items))
      .catch(() => setError(t("myGameweeks.errors.failedToLoad")))
      .finally(() => setLoading(false));
  }, [user, t]);

  if (loading) return <PageLoader />;

  return (
    <div style={{ ...widePageContainer, padding: "1.5rem 0" }}>
      <h1 style={pageTitle}>{t("myGameweeks.title")}</h1>

      {error && <p style={errorStyle}>{error}</p>}

      {!error && items.length === 0 ? (
        <div style={card}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>{t("myGameweeks.empty")}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {items.map((item) => (
            <Link
              key={item.gameweekId}
              to={`/my-gameweeks/${item.competitionId}/${item.gameweekId}`}
              style={{
                ...card,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                gap: "0.5rem",
                textDecoration: "none",
                color: "var(--color-text)",
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{item.competitionName}</div>
                <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {item.gameweekName}
                </div>
              </div>
              <span
                style={{
                  ...badge,
                  background: item.completedAt ? "var(--color-surface-alt)" : "var(--color-status-active-bg)",
                  color: item.completedAt ? "var(--color-text-muted)" : "var(--color-status-active-text)",
                }}
              >
                {item.completedAt ? t("myGameweeks.status.completed") : t("myGameweeks.status.open")}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** Standalone wrapper for /my-gameweeks/:competitionId/:gwId — reads competitionId from the URL
 * instead of AdminCompetitionPage's outlet context, sets its own breadcrumb trail (nothing above
 * it on this route sets one), and overrides GameweekOpsPage's default owner-facing back-link. */
export function MyGameweekOpsRoute({ user }: Props) {
  const { t } = useTranslation();
  const { competitionId } = useParams<{ competitionId: string; gwId: string }>();

  useBreadcrumbs([
    { label: t("common.home"), to: "/overview" },
    { label: t("myGameweeks.title"), to: "/my-gameweeks" },
    { label: t("myGameweeks.gameweekCrumb") },
  ]);

  if (!competitionId) return null;

  return (
    <div style={{ ...widePageContainer, padding: "1.5rem 0" }}>
      <GameweekOpsPage user={user} competitionId={competitionId} backTo="/my-gameweeks" />
    </div>
  );
}
